import { useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageHeader, Panel } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, useFetch } from "@/lib/api";
import { ALL_TSP_FUNDS, fundColor, FUND_META } from "@/lib/finance";
import { changeClass, num, pct } from "@/lib/format";
import { Seo } from "@/components/Seo";
import site from "@/content/site.json";

function FundGuide() {
  return (
    <section className="mt-10 max-w-4xl" data-testid="fund-guide">
      <h2 className="font-display text-2xl font-semibold">Guide to the TSP funds</h2>
      <p className="text-sm text-muted-foreground mt-2">What each Thrift Savings Plan fund invests in, and how it tends to behave.</p>
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {site.funds.guide.map((g) => (
          <div key={g.fund} className="glass p-5" data-testid={`fund-guide-${g.fund.split(" ")[0].toLowerCase()}`}>
            <h3 className="font-display font-semibold">{g.fund}</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 leading-relaxed">{g.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const RANGES = [["1M", 30], ["3M", 90], ["1Y", 365], ["2Y", 730]];

function History({ fund, days }) {
  const { data, loading } = useFetch(`/prices/history?fund=${encodeURIComponent(fund)}&days=${days}`);
  if (loading) return <div className="h-80 grid place-items-center text-sm text-muted-foreground">Loading history...</div>;
  const first = data?.[0]?.price, last = data?.[data.length - 1]?.price;
  const color = fundColor(fund);
  return (
    <div>
      <p className={`font-mono-num text-sm mb-3 ${changeClass(last - first)}`} data-testid="history-range-change">{first ? pct(((last - first) / first) * 100) : "—"} over range · {data?.length || 0} closes</p>
      <div className="h-80" data-testid="fund-history-chart">
        <ResponsiveContainer>
          <AreaChart data={data}>
            <defs><linearGradient id="fh" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.3} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid vertical={false} strokeOpacity={0.25} />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={40} stroke="#94A3B8" />
            <YAxis domain={["auto", "auto"]} tick={{ fontSize: 11 }} width={56} stroke="#94A3B8" />
            <Tooltip formatter={(v) => `$${num(v)}`} />
            <Area dataKey="price" stroke={color} strokeWidth={2} fill="url(#fh)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Intraday() {
  const { data, loading } = useFetch("/prices/intraday");
  if (loading) return <p className="text-sm text-muted-foreground">Loading proxies...</p>;
  return (
    <table className="w-full text-sm" data-testid="intraday-table">
      <thead className="text-xs text-muted-foreground"><tr><th className="text-left py-2 font-medium">Fund</th><th className="text-left font-medium">Benchmark proxy</th><th className="text-right font-medium">Proxy move</th><th className="text-right font-medium">Last official</th><th className="text-right font-medium">Est. tonight</th></tr></thead>
      <tbody>{data?.map((r) => (
        <tr key={r.fund} className="border-t border-border/60" data-testid={`intraday-row-${r.fund}`}>
          <td className="py-2"><span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: fundColor(r.fund) }} />{r.fund} Fund</span></td>
          <td className="text-xs text-muted-foreground">{r.proxy}{r.symbol ? ` · ${r.symbol}` : ""}</td>
          <td className={`text-right font-mono-num ${changeClass(r.proxy_change_pct)}`}>{pct(r.proxy_change_pct)}</td>
          <td className="text-right font-mono-num">${num(r.official_price)}</td>
          <td className="text-right font-mono-num font-medium">${num(r.estimated_price)}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

export default function Funds() {
  const { user } = useAuth();
  const latest = useFetch("/prices/latest");
  const [fund, setFund] = useState("C");
  const [days, setDays] = useState(365);
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    setBusy(true);
    try { const r = await api.post("/prices/refresh"); toast.success(`Fetched ${r.data.rows} days from tsp.gov (${r.data.new_records} new)`); latest.reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const prices = (latest.data?.prices || []).sort((a, b) => ALL_TSP_FUNDS.indexOf(a.fund) - ALL_TSP_FUNDS.indexOf(b.fund));
  const run = latest.data?.last_run;
  return (
    <div>
      <Seo title={site.funds.title} description={site.funds.description} path="/funds" />
      <PageHeader title="TSP Fund Prices" subtitle="Official daily share prices from tsp.gov, logged nightly after settlement. Intraday estimates use benchmark index proxies until tonight's official price posts."
        actions={user && <Button variant="outline" className="rounded-full gap-2" onClick={refresh} disabled={busy} data-testid="prices-refresh-button"><ArrowsClockwise size={16} className={busy ? "animate-spin" : ""} /> Fetch official prices</Button>} />
      {run && <p className="text-xs text-muted-foreground mb-4 font-mono-num" data-testid="last-price-run">Last ingestion run: {new Date(run.ran_at).toLocaleString()} · {run.new} new records</p>}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Panel title="Official closing prices" testid="panel-official-prices">
          <table className="w-full text-sm" data-testid="official-prices-table">
            <thead className="text-xs text-muted-foreground"><tr><th className="text-left py-2 font-medium">Fund</th><th className="text-right font-medium">Price</th><th className="text-right font-medium">1D</th></tr></thead>
            <tbody>{prices.map((p) => (
              <tr key={p.fund} onClick={() => setFund(p.fund)} className={`border-t border-border/60 cursor-pointer transition-colors ${fund === p.fund ? "bg-cyan-500/10" : "hover:bg-slate-900/5 dark:hover:bg-white/5"}`} data-testid={`price-row-${p.fund.replace(" ", "-")}`}>
                <td className="py-2 pl-1"><span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: fundColor(p.fund) }} />{p.fund.length === 1 ? `${p.fund} Fund` : p.fund}</span></td>
                <td className="text-right font-mono-num">${num(p.price)}</td>
                <td className={`text-right font-mono-num pr-1 ${changeClass(p.change_pct)}`}>{pct(p.change_pct)}</td>
              </tr>
            ))}</tbody>
          </table>
          <p className="text-xs text-muted-foreground mt-3 font-mono-num">As of {prices[0]?.date}</p>
        </Panel>
        <Panel title={`${fund.length === 1 ? FUND_META[fund].name : fund} history`} className="xl:col-span-2" testid="panel-fund-history"
          action={<div className="flex gap-1">{RANGES.map(([l, d]) => <Button key={l} size="sm" variant={days === d ? "default" : "ghost"} onClick={() => setDays(d)} data-testid={`range-${l}`}>{l}</Button>)}</div>}>
          <History fund={fund} days={days} />
        </Panel>
        <Panel title="Intraday proxy estimates" className="xl:col-span-3" action={<Badge variant="outline" className="text-[10px]">Estimate · not official</Badge>} testid="panel-intraday"><Intraday /></Panel>
      </div>
      <FundGuide />
    </div>
  );
}
